import {Button} from "@zendeskgarden/react-buttons";
import {Checkbox, Field, Hint, Label, Toggle} from "@zendeskgarden/react-forms";
import {Body, Close, Footer, FooterItem, Header, Modal} from "@zendeskgarden/react-modals";
import {ReactElement, useMemo, useState} from "react";
import {VersionType} from "../../../__generated__/generated_types";
import {OperationalMetricTypeIds} from "../../../constants/OperationalMetricTypeIds";
import {pluralize} from "../../../utils/string-helpers";
import MinMaxFields, {transformMinMaxValueToDecimalString} from "../../workflows/account/formula-bar/components/formula-nodes/components/MinMaxFields";


export interface OpDriverConfigItem {
    propertyId: string;
    year: number;
    versionType: VersionType;
    destinationAccountId: string;
    sourceMetricId: string;
    name: string;
    minValue: number | null;
    maxValue: number | null;
    cumSum: boolean;
    nonProrated: boolean;
}

interface OpDriverConfigItemForUpdate extends Omit<OpDriverConfigItem, 'minValue' | 'maxValue' | 'cumSum' | 'nonProrated'> {
    minValue?: string | null;
    maxValue?: string | null;
    cumSum?: boolean;
    nonProrated?: boolean;
}

interface OpDriverConfigModalProps {
    drivers: OpDriverConfigItem[];
    isBusySaving: boolean;
    onSave: (items: OpDriverConfigItemForUpdate[]) => void;
    onClose: () => void;
}

export default function OpDriverConfigModal(props: OpDriverConfigModalProps): ReactElement {
    const driverCumSums = [];
    const driverNonProrateds = [];
    const driverMinValues: Set<number | null> = new Set();
    const driverMaxValues: Set<number | null> = new Set();

    for (const driver of props.drivers) {
        driverCumSums.push(driver.cumSum);
        driverNonProrateds.push(driver.nonProrated);
        driverMinValues.add(driver.minValue);
        driverMaxValues.add(driver.maxValue);
    }

    const initialCumSum = driverCumSums.every(x => x === true);
    const initialProrated = driverNonProrateds.every(x => x === false);
    const isEmployeeCountSelection = props.drivers.every(d => d.sourceMetricId === OperationalMetricTypeIds.EMPLOYEE_COUNT);
    let initialMinValue = null;
    let initialMaxValue = null;

    if (driverMinValues.size === 1) {
        initialMinValue = Array.from(driverMinValues)[0] ?? null;
        initialMinValue = initialMinValue === null ? null : String(initialMinValue);
    }
    if (driverMaxValues.size === 1) {
        initialMaxValue = Array.from(driverMaxValues)[0] ?? null;
        initialMaxValue = initialMaxValue === null ? null : String(initialMaxValue);
    }

    const [minValue, setMinValue] = useState<string | null>(initialMinValue);
    const [maxValue, setMaxValue] = useState<string | null>(initialMaxValue);
    const [isCumSum, setIsCumSum] = useState(initialCumSum);
    const [shouldUpdateCumSum, setShouldUpdateCumSum] = useState(false);
    const [isProrated, setIsProrated] = useState(initialProrated);
    const [shouldUpdateProrated, setShouldUpdateProrated] = useState(false);
    const [shouldUpdateMinMax, setShouldUpdateMinMax] = useState(false);
    const [minMaxError, setMinMaxError] = useState<string | null>(null);

    const isBusyWorking = useMemo(() => {
        let isDisabled = false;
        const selectedFieldsToUpdate = [shouldUpdateCumSum, shouldUpdateProrated, shouldUpdateMinMax];

        // if the api is not saving,
        // at least one box is checked,
        // if the minMax box is checked AND the minMaxError is empty,
        // then the user can click the cancel/save buttons
        if (props.isBusySaving ||
            selectedFieldsToUpdate.every(x => x === false) ||
            (shouldUpdateMinMax && minMaxError !== null)
        ) {
            isDisabled = true;
        }

        return isDisabled;
    }, [minMaxError, shouldUpdateCumSum, shouldUpdateProrated, shouldUpdateMinMax, props.isBusySaving]);

    function handleSave() {
        // build the items to save by starting with the required keys for updating a driver,
        // and then dynamically add the keys for the selected configuration options
        // this lets a user update just the ramp up, or just the min/max without changing the other
        // if we want/need to spend more time here, we can also separate min and max BUT that would
        // mean finding the min and max values across all selected drivers, and then determining error state
        // based on those plus which fields they have selected to save, so it's a bigger solve than just this quick add
        const itemsToSave: OpDriverConfigItemForUpdate[] = [];

        for (const driver of props.drivers) {
            const dataToSave: Partial<OpDriverConfigItemForUpdate> = {};

            if (shouldUpdateCumSum) {
                dataToSave.cumSum = isCumSum;
            }

            if (shouldUpdateProrated) {
                dataToSave.nonProrated = !isProrated;
            }

            if (shouldUpdateMinMax) {
                dataToSave.minValue = transformMinMaxValueToDecimalString(minValue);
                dataToSave.maxValue = transformMinMaxValueToDecimalString(maxValue);
            }

            itemsToSave.push({
                propertyId: driver.propertyId,
                year: driver.year,
                versionType: driver.versionType,
                destinationAccountId: driver.destinationAccountId,
                sourceMetricId: driver.sourceMetricId,
                name: driver.name,
                ...dataToSave,
            })
        }
        props.onSave(itemsToSave);
    }

    return (
        <Modal onClose={props.onClose}>
            <Header>Set Configuration Options for {props.drivers.length} {pluralize(props.drivers.length, "Driver")}</Header>
            <Body>
                <div>
                    <h5>Select the settings you'd like to update.</h5>
                    <Field>
                        <Checkbox checked={shouldUpdateCumSum} onChange={evt => setShouldUpdateCumSum(evt.target.checked)}>
                            <Label>Update Ramp-Up</Label>
                            <Hint>This will make each month carry over the previous month's total.</Hint>
                        </Checkbox>
                    </Field>

                    {shouldUpdateCumSum &&
                        <Field style={{marginBottom: "1rem", marginTop: ".5rem", paddingLeft: "1.5rem"}}>
                            <Toggle
                                isCompact
                                checked={isCumSum}
                                onChange={evt => setIsCumSum(evt.target.checked)}
                            >
                                <Label>Amounts Ramp-up</Label>
                            </Toggle>
                        </Field>
                    }

                    {isEmployeeCountSelection &&
                        <Field>
                            <Checkbox checked={shouldUpdateProrated} onChange={evt => setShouldUpdateProrated(evt.target.checked)}>
                                <Label>Update Employee Proration</Label>
                                <Hint>This will prorate the employee count based on their start date.</Hint>
                            </Checkbox>
                        </Field>
                    }

                    {isEmployeeCountSelection && shouldUpdateProrated &&
                        <Field style={{marginBottom: "1rem", marginTop: ".5rem", paddingLeft: "1.5rem"}}>
                            <Toggle
                                isCompact
                                checked={isProrated}
                                onChange={evt => setIsProrated(evt.target.checked)}
                            >
                                <Label>{isProrated ? "Prorated" : "Full Count"}</Label>
                            </Toggle>
                        </Field>
                    }

                    <Field>
                        <Checkbox checked={shouldUpdateMinMax} onChange={evt => setShouldUpdateMinMax(evt.target.checked)}>
                            <Label>Update Min and Max</Label>
                        </Checkbox>
                    </Field>
                </div>

                {shouldUpdateMinMax &&
                    <div style={{paddingLeft: "1.5rem"}}>
                        <MinMaxFields
                            minValue={minValue}
                            maxValue={maxValue}
                            onChange={vals => {
                                setMinValue(vals.minValue);
                                setMaxValue(vals.maxValue);
                            }}
                            onErrorUpdate={setMinMaxError}
                        />
                    </div>
                }
            </Body>
            <Footer>
                <FooterItem>
                    <Button onClick={props.onClose} isBasic disabled={isBusyWorking}>
                        Cancel
                    </Button>
                </FooterItem>
                <FooterItem>
                    <Button
                        isPrimary
                        onClick={handleSave}
                        disabled={isBusyWorking}
                    >
                        Save
                    </Button>
                </FooterItem>
            </Footer>
            <Close aria-label="Close modal"/>
        </Modal>
    );
}
